import { AppLink } from "@/components/ui/app-link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalRelative } from "@/components/ui/local-date";
import { api } from "@/lib/api";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/** `/dashboard` — where a client lands: their projects and how the work on them stands. */

type ClientProject = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  openCount: number;
  closedCount: number;
  open: Array<{ name: string; color: string; count: number }>;
  lastActivityAt: string | null;
};

export const Route = createFileRoute("/_app/dashboard")({
  loader: async ({ parentMatchPromise }) => {
    if (!(await parentMatchPromise).loaderData?.isCustomer) throw redirect({ to: "/my" });
    return api.get<{ projects: ClientProject[] }>("/api/me/client-projects");
  },
  component: ClientDashboard,
});

function ClientDashboard() {
  const { t } = useTranslation();
  const { projects } = Route.useLoaderData();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold">{t("dashboard.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("dashboard.subtitle")}</p>
      </div>
      {projects.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("dashboard.noProjects")}</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="client-projects">
          {projects.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectCard({ project: p }: { project: ClientProject }) {
  const { t } = useTranslation();
  const workItemsHref = `/projects/${p.slug}/work-items`;
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle>
          <AppLink href={workItemsHref} className="hover:underline">
            {p.name}
          </AppLink>
        </CardTitle>
        {p.description && (
          <CardDescription className="line-clamp-2">{p.description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="mt-auto space-y-3">
        <p className="text-sm">
          <span className="font-semibold">{p.openCount}</span> {t("dashboard.open")} ·{" "}
          <span className="text-muted-foreground">
            {p.closedCount} {t("dashboard.done")}
          </span>
        </p>
        {p.open.length > 0 && (
          <ul className="flex flex-wrap gap-1.5">
            {p.open.map((s) => (
              <li
                key={s.name}
                className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs"
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                {t(`dashboard.status.${s.name}`, { defaultValue: s.name.replace(/_/g, " ") })}{" "}
                {s.count}
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            {p.lastActivityAt ? (
              <>
                {t("dashboard.lastChange")} <LocalRelative value={p.lastActivityAt} />
              </>
            ) : (
              t("dashboard.noRequests")
            )}
          </span>
          <span className="flex gap-3">
            <AppLink href={workItemsHref} className="underline">
              {t("dashboard.workItems")}
            </AppLink>
            <AppLink href={`/projects/${p.slug}/wiki`} className="underline">
              {t("dashboard.wiki")}
            </AppLink>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
